<template>
  <div class="flex flex-col">
    <app-header class="pl-4 sticky top-0 z-10">
      <filter-menu :show-channels="true"
                   :show-granularity="true"
                   :is-loading="loaders.filters"
                   @filter-change="onFilterChanged"
      />
    </app-header>

    <analytics-charts :loaders="loaders"
                      :bot-id="botId"
                      class="my-12 w-11/12 xl:w-full z-0"
                      @load-assistance-resolution="handleAssistanceResolutions"
    />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader'
import FilterMenu from './filter/FilterMenu'
import AnalyticsCharts from '@/components/analytics/charts/AnalyticsCharts'
import analyticsService from '@/services/amio/analytics.service'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import channelModule from '@/store/modules/channel.module'
import botModule from '@/store/modules/bot.module'
import toaster from '@/utils/toaster'
import cacheModule from '@/store/modules/cache.module'
import analyticsModule from '@/store/modules/analytics.module'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import AssistanceResolutionsUtils from '@/utils/assistance-resolutions.utils'
import { FILTER_ITEMS, GRANULARITY_CONTACT, GRANULARITY_REQUEST } from '@/utils/filter.constants'

export default {
  name: 'Analytics',

  metaInfo: {
    title: 'Analytics'
  },

  components: {
    AnalyticsCharts,
    AppHeader,
    FilterMenu
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loaders: {
        requestTotal: false,
        automationRate: false,
        intentUsages: false,
        assistanceResolutions: false,
        assistanceResolutionsUpdate: false,
        filters: false
      },
      requestOutcomes: null,
      assistanceResolutionsUtils: null
    }
  },

  computed: {
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    channels: analyticsFilterModule.getChannels,
    granularity: analyticsFilterModule.getGranularity,
    requests: analyticsModule.getRequests,
    contacts: analyticsModule.getContacts,
    outcomes: analyticsModule.getOutcomes,
    answers: answerModule.getAnswerList,

    isEmailBot() {
      return botModule.isEmailBot()
    }
  },

  methods: {
    // API calls
    async retrieveAnalytics() {
      const chartLoaders = ['requestTotal', 'automationRate', 'intentUsages', 'assistanceResolutions']
      chartLoaders.forEach(loader => {
        this.setLoader(loader, true)
      })

      try {
        // API call
        await this.retrieveRequestOutcomes()
        // setters + mappers
        await this.initChartData()
      } catch (e) {
        toaster.error(e)
      }
    },

    async retrieveChannelList(botId) {
      analyticsFilterModule.setChannels(null)
      await this.retrieveFiltersData(botId)
    },

    async retrieveRequestOutcomes() {
      const params = {
        botId: this.botId,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        channels: this.channels
      }
      const { requestOutcomes, outcomes } = await analyticsService.getRequestOutcomes(params)

      analyticsModule.setOutcomes(requestOutcomeMapper.buildOutcomeMap(outcomes))
      this.requestOutcomes = Object.freeze(requestOutcomes)
    },

    async retrieveFiltersData(botId) {
      this.setLoader('filters', true)
      try {
        await Promise.all([
          answerModule.setAnswerList(await answerService.list(this.botId)),
          analyticsFilterModule.setBot(botId),
          channelModule.setChannelList(botId),
          botModule.setBotList()
        ])
      } catch (err) {
        toaster.error(err)
      }
      this.setLoader('filters', false)
    },

    // init chart data
    async initChartData() {
      await this.initAggregatedAutomationOutcomes()
      await this.initTotals()
      await this.initIntentUsages()
      await this.initAssistanceResolutions()
    },

    async initAggregatedAutomationOutcomes() {
      const setter = () => {
        const requests = requestOutcomeMapper.mapRequestOutcomes(this.requestOutcomes, this.outcomes)
        analyticsModule.setRequests(requests)

        if (GRANULARITY_REQUEST === this.granularity) {
          const aggregatedAutomationOutcomes = requestOutcomeMapper.mapAutomationRateData(this.requests, this.isEmailBot)
          analyticsModule.setAggregatedAutomationOutcomes(aggregatedAutomationOutcomes)
        } else {
          const contacts = requestOutcomeMapper.mapContactOutcomes(requests)
          analyticsModule.setContacts(contacts)
          const aggregatedAutomationOutcomes = requestOutcomeMapper.mapAutomationRateData(this.contacts, this.isEmailBot)
          analyticsModule.setAggregatedAutomationOutcomes(aggregatedAutomationOutcomes)
        }
      }
      await this.resolveSetter(setter, 'automationRate')
    },

    async initTotals() {
      const setter = () => {
        if (GRANULARITY_CONTACT === this.granularity) {
          const contactTotal = requestOutcomeMapper.calculateContactTotal(this.contacts)
          analyticsModule.setContactTotal(contactTotal)
        }
        const requestTotal = requestOutcomeMapper.calculateRequestTotal(this.requests)
        analyticsModule.setRequestTotal(requestTotal)
      }
      await this.resolveSetter(setter, 'requestTotal')
    },

    async initIntentUsages() {
      const setter = () => {
        const intentUsages = requestOutcomeMapper.mapAnswerUsages(this.answers, this.requests, false, this.isEmailBot)
        analyticsModule.setIntentUsages(intentUsages)
      }
      await this.resolveSetter(setter, 'intentUsages')
    },

    async initAssistanceResolutions() {
      const from = +new Date(this.dateFrom)
      const to = +new Date(this.dateTo)
      const { timeGranularity } = this.assistanceResolutionsUtils.getTimeGranularityParams(from, to)
      await this.setAssistanceResolutions(from, to, timeGranularity, 'assistanceResolutions')
    },

    async setAssistanceResolutions(from, to, timeGranularity, loader) {
      const setter = () => {
        const intervals = requestOutcomeMapper.mapAssistanceResolutionIntervals(
          this.requests,
          from,
          to,
          timeGranularity
        )
        const assistanceResolutions = requestOutcomeMapper.mapAssistanceResolutionsData(
          intervals
        )
        analyticsModule.setAssistanceResolutions(assistanceResolutions)
      }

      await this.resolveSetter(setter, loader)
    },

    // handlers
    async changeBot(bot) {
      if (this.botId === bot.id) return

      botModule.setBot(bot)
      await this.$router.push({
        name: 'analytics',
        params: { botId: bot.id },
        query: { dateFrom: this.dateFrom, dateTo: this.dateTo, granularity: this.granularity }
      })
      await this.retrieveChannelList(bot.id)
    },

    async onFilterChanged(filterValues) {
      analyticsFilterModule.setDateFrom(filterValues[FILTER_ITEMS.DATE_FROM])
      analyticsFilterModule.setDateTo(filterValues[FILTER_ITEMS.DATE_TO])
      analyticsFilterModule.setGranularity(filterValues[FILTER_ITEMS.GRANULARITY])
      analyticsFilterModule.setChannels(filterValues[FILTER_ITEMS.CHANNELS])
      await this.changeBot(filterValues[FILTER_ITEMS.BOT])
      await this.retrieveAnalytics()
    },

    async handleAssistanceResolutions({ from, to }) {
      const params = this.assistanceResolutionsUtils.getTimeGranularityParams(
        +new Date(from),
        +new Date(to),
        +new Date(this.dateFrom),
        +new Date(this.dateTo)
      )
      if (!params) return

      const { dateFrom, dateTo, timeGranularity } = params
      await this.setAssistanceResolutions(dateFrom, dateTo, timeGranularity, 'assistanceResolutionsUpdate')
    },

    // utils
    async resolveSetter(setter, loader) {
      if (loader === 'assistanceResolutionsUpdate') this.setLoader('assistanceResolutionsUpdate', true)
      // hack, some time for starting loader on chart, before crunching of data starts,
      // otherwise no loader appears on chart and javascript just runs, without any UI change
      // note: .then(() => setter()), nextTick and Promise.resolve don't do a trick
      await this.sleep(100)
      await setter()
      this.setLoader(loader, false)
    },

    setLoader(loader, isLoading) {
      this.$set(this.loaders, loader, isLoading)
    },

    createAssistanceResolutionsUtils() {
      this.assistanceResolutionsUtils = new AssistanceResolutionsUtils(+new Date(this.dateFrom), +new Date(this.dateTo))
    }

  },

  mounted() {
    this.createAssistanceResolutionsUtils()
    this.retrieveAnalytics()
  },

  created() {
    this.retrieveFiltersData(this.botId)
  },

  beforeDestroy() {
    cacheModule.resetCaches()
  }
}
</script>
